import React from 'react'
import { useTheme } from 'styled-components'
import {
	Grid,
	Typography,
	useMediaQuery,
	Theme,
	IconButton,
	Skeleton
} from '@material-ui/core'
import PinIcon from 'assets/icons/PinIcon'
import PinIconOutlined from 'assets/icons/PinIconOutlined'
import BookmarkBorderIcon from '@material-ui/icons/BookmarkBorder'
import BookmarkIcon from '@material-ui/icons/Bookmark'
import CircleProgressBar from 'components/CircleProgressBar'
import ContrastChip from 'components/ContrastChip'
import LinearProgress from 'components/LinearProgress'
import { useIntl } from 'hooks/useIntl'
import { getInitials } from 'utils/string'
import { EnumPipelineType } from 'hooks/useApollo'
import { differenceInDays, format, formatDistanceToNow } from 'date-fns'
import { ProfileCardContainer, Avatar } from './ProfileCard.styles'

export interface ProfileCardProps {
	photoUrl?: string
	firstName: string
	lastName: string
	groups?: { name: string; color: string; id: string }[]
	score?: number
	progress: number
	type?: EnumPipelineType
	isSelected?: boolean
	isBookmarked?: boolean
	masked?: boolean
	onClick: () => void
	onBookmark: () => void
	addedAt: string
	isPinned?: boolean
	onPin: () => void
	skillId?: string
	skills?: { id: string; score: number }[]
}
export const ProfileCard: React.FC<ProfileCardProps> = ({
	photoUrl,
	firstName,
	lastName,
	groups = [],
	score,
	progress,
	type,
	isSelected = false,
	isBookmarked = false,
	masked = false,
	onClick,
	onBookmark,
	addedAt: addedAtProp,
	isPinned,
	onPin,
	skills = [],
	skillId
}) => {
	const theme = useTheme()
	const intl = useIntl()
	const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'))
	const smUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
	const addedAt = new Date(addedAtProp)
	const timeDiff =
		differenceInDays(Date.now(), addedAt) > 7
			? format(addedAt, 'MMM dd, yyyy')
			: formatDistanceToNow(addedAt, {
					addSuffix: true,
					includeSeconds: true
			  })

	const isSpecialSkill = skillId === '67dbf4d42c98d18c9d68381f'
	const skillScore = skills.find(skill => skill.id === skillId)?.score
	const displayScore =
		(progress === 100 || isSpecialSkill) && skillScore !== undefined ? skillScore : score

	return (
		<ProfileCardContainer isSelected={isSelected} onClick={onClick}>
			<Grid item container spacing={3}>
				<Grid item container spacing={2} wrap="nowrap">
					<Grid
						item
						container
						spacing={2}
						direction={smUp ? 'row' : 'column'}
						alignItems={smUp ? 'center' : 'flex-start'}
					>
						<Grid item>
							<Avatar src={photoUrl} lgUp={lgUp}>
								{getInitials(firstName, lastName)}
							</Avatar>
						</Grid>
						<Grid container item xs>
							<Grid item container>
								<Grid item>
									<Typography
										variant="paragraph1"
										fontWeight={500}
										sx={{ color: theme.tag.darkerBlue, mr: 0.5 }}
									>
										{masked ? (
											<Skeleton width={80} animation={false} />
										) : (
											`${firstName} ${lastName}`
										)}
									</Typography>
								</Grid>
								<Grid item>
									<IconButton
										size="small"
										onClick={e => {
											e.stopPropagation()
											onBookmark()
										}}
									>
										{isBookmarked ? (
											<BookmarkIcon color="primary" sx={{ fontSize: '1.3rem' }} />
										) : (
											<BookmarkBorderIcon
												sx={{ fontSize: '1.3rem', color: theme.text.description }}
											/>
										)}
									</IconButton>
								</Grid>
								<Grid item>
									<IconButton
										size="small"
										onClick={e => {
											e.stopPropagation()
											onPin()
										}}
									>
										{isPinned ? (
											<PinIcon
												sx={{
													fill: theme.tag.red,
													fontSize: '1.2rem'
												}}
											/>
										) : (
											<PinIconOutlined
												sx={{ fill: theme.background.light, fontSize: '1.2rem' }}
											/>
										)}
									</IconButton>
								</Grid>
							</Grid>
							<Grid container item>
								<Grid item>
									{type && (
										<Typography
											variant="paragraph2"
											sx={{ textTransform: 'capitalize', color: theme.text.description }}
										>
											{intl.formatMessage({
												id: type === 'Internal' ? 'employee' : 'candidate'
											})}
											&nbsp;
										</Typography>
									)}
								</Grid>
								<Grid item>
									{addedAt && (
										<Typography variant="paragraph2" sx={{ color: theme.text.description }}>
											{lgUp && addedAt ? ' • ' : ''}
											{timeDiff}
										</Typography>
									)}
								</Grid>
							</Grid>
						</Grid>
					</Grid>

					<Grid item>
						{progress == 100 && displayScore !== undefined ? (
							<>
								<CircleProgressBar
									stroke={theme.tag.blue}
									background={theme.progressBarBg}
									percentage={displayScore}
									diameter={72}
									strokeWidth={6}
								/>
								<Typography align="center" variant="paragraph3">
									{intl.formatMessage({ id: 'nuggetScore' })}
								</Typography>
							</>
						) : null}
					</Grid>
				</Grid>
				<Grid container item alignItems="center">
					{groups && (
						<Grid item container xs mb={1} spacing={1}>
							{groups.slice(0, 2).map(group => (
								<Grid item key={group.id}>
									<ContrastChip color={group.color} key={group.id} label={group.name} />
								</Grid>
							))}
							{groups.length > 2 && (
								<Grid item>
									<Typography variant="paragraph2" sx={{ color: theme.text.description }}>
										+ {groups.length - 2}
									</Typography>
								</Grid>
							)}
						</Grid>
					)}
					<Grid item sx={{ width: 140 }}>
						<LinearProgress value={progress} labeled="left" />
					</Grid>
				</Grid>
			</Grid>
		</ProfileCardContainer>
	)
}

export default ProfileCard
